import { useState, useEffect } from 'react';
import { Users, Save } from 'lucide-react';
import { SITE_NAME, SITE_SHORT_NAME } from '@/config/site';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

type RewardType = 'percentage' | 'fixed' | 'points' | 'free_month';
type CompletionTrigger = 'first_paid_subscription' | 'first_paid_order' | 'either';

interface ReferralConfig {
  enabled: boolean;
  referrer_reward_type: RewardType;
  referrer_reward_value: number;
  referrer_reward_duration: number;
  referee_reward_type: RewardType;
  referee_reward_value: number;
  referee_reward_duration: number;
  minimum_purchase: number;
  completion_trigger: CompletionTrigger;
  share_message: string;
  email_subject: string;
  email_template: string;
  stripe_referrer_coupon_id?: string | null;
  stripe_referee_coupon_id?: string | null;
}

const DEFAULTS: ReferralConfig = {
  enabled: false,
  referrer_reward_type: 'free_month',
  referrer_reward_value: 0,
  referrer_reward_duration: 1,
  referee_reward_type: 'free_month',
  referee_reward_value: 0,
  referee_reward_duration: 1,
  minimum_purchase: 0,
  completion_trigger: 'either',
  share_message: `Join me at ${SITE_SHORT_NAME}! Use my referral link to get a special reward.`,
  email_subject: `Try ${SITE_NAME} on me`,
  email_template: `Your friend {{referrer_name}} thinks you'll love ${SITE_SHORT_NAME}! Sign up with this link to claim your reward.`,
  stripe_referrer_coupon_id: null,
  stripe_referee_coupon_id: null,
};

// Free-month rewards don't take a user-entered value; the duration is the only
// thing that matters. Hide the Value input for that type to avoid confusion.
function rewardTypeUsesValue(type: RewardType): boolean {
  return type !== 'free_month';
}

function rewardTypeUsesDuration(type: RewardType): boolean {
  return type !== 'points';
}

export default function UpsellReferrals() {
  const [config, setConfig] = useState<ReferralConfig>(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [stats, setStats] = useState<{
    total_referrals: number;
    successful_referrals: number;
    conversion_rate: number;
  } | null>(null);

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, []);

  async function fetchConfig() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      // The customer-side reads upsell_config.referral_program (matches the
      // default seeded in migration 009). Earlier versions of this admin page
      // wrote/read upsell_config.referrals — that mismatch silently broke the
      // toggle for every tenant. Read both for compatibility with any old
      // rows that still have the wrong key.
      const upsell = response.settings.upsell_config || {};
      const saved = upsell.referral_program ?? upsell.referrals;
      if (saved) {
        setConfig({ ...DEFAULTS, ...saved });
      }
    } catch (err) {
      setError('Failed to load referral program settings. Please try again.');
      console.error('Error fetching referral config:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchStats() {
    try {
      const response = await api.get<{ stats: any }>('/admin/referrals/stats');
      setStats(response.stats || { total_referrals: 0, successful_referrals: 0, conversion_rate: 0 });
    } catch (err) {
      console.error('Error fetching referral stats:', err);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    try {
      setIsSaving(true);
      setError(null);
      const currentSettings = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const currentUpsellConfig = currentSettings.settings.upsell_config || {};

      await api.put('/admin/settings', {
        settings: {
          upsell_config: {
            ...currentUpsellConfig,
            referral_program: config,
          },
        },
      });

      // Refresh Stripe coupons so a value/type change re-mints them. Failures
      // here don't roll back the config save — the admin can re-trigger
      // provisioning later, or fall back to points (which needs no Stripe).
      try {
        const provision = await api.post<{ changed: boolean; errors: string[] }>(
          '/admin/referrals/provision-coupons',
          {},
        );
        if (provision?.errors && provision.errors.length > 0) {
          setSuccessMessage(
            `Saved — coupon provisioning had warnings: ${provision.errors.join('; ')}`,
          );
        } else if (provision?.changed) {
          setSuccessMessage('Settings saved and Stripe coupons refreshed.');
        } else {
          setSuccessMessage('Settings saved.');
        }
      } catch (provisionErr) {
        console.error('Coupon provision failed (settings saved):', provisionErr);
        setSuccessMessage('Settings saved — coupon provisioning failed (see server logs).');
      }

      await fetchConfig();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving referral config:', err);
    } finally {
      setIsSaving(false);
    }
  }

  function handleChange<K extends keyof ReferralConfig>(field: K, value: ReferralConfig[K]) {
    setConfig(prev => ({ ...prev, [field]: value }));
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading referral program settings...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Users className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Referral Program</h1>
        </div>
        <p className="text-gray-600">Incentivize customers to refer friends and family</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {stats && (
        <Card title="Referral Stats" padding="lg" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-stone-600 mb-1">Total Referrals</p>
              <p className="text-3xl font-bold text-stone-900">{stats.total_referrals}</p>
            </div>
            <div>
              <p className="text-sm text-stone-600 mb-1">Successful Referrals</p>
              <p className="text-3xl font-bold text-green-700">{stats.successful_referrals}</p>
            </div>
            <div>
              <p className="text-sm text-stone-600 mb-1">Conversion Rate</p>
              <p className="text-3xl font-bold text-green-700">{stats.conversion_rate.toFixed(1)}%</p>
            </div>
          </div>
        </Card>
      )}

      <form onSubmit={handleSave} noValidate>
        <div className="space-y-6">
          <Card title="Enable Program" padding="lg">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="referral_enabled"
                checked={config.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="referral_enabled" className="text-sm font-medium text-gray-700">
                Enable customer referral program
              </label>
            </div>
          </Card>

          <Card title="Completion Trigger" padding="lg">
            <div className="space-y-2">
              <p className="text-sm text-stone-600">
                When does a referral count as successful?
              </p>
              <label htmlFor="completion_trigger" className="sr-only">
                Completion trigger
              </label>
              <select
                id="completion_trigger"
                value={config.completion_trigger}
                onChange={(e) => handleChange('completion_trigger', e.target.value as CompletionTrigger)}
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="first_paid_subscription">First paid subscription invoice (SaaS)</option>
                <option value="first_paid_order">First paid order (ecommerce)</option>
                <option value="either">Either — paid order or subscription</option>
              </select>
            </div>
          </Card>

          <Card title="Referrer Rewards" padding="lg">
            <RewardEditor
              type={config.referrer_reward_type}
              value={config.referrer_reward_value}
              duration={config.referrer_reward_duration}
              onTypeChange={(v) => handleChange('referrer_reward_type', v)}
              onValueChange={(v) => handleChange('referrer_reward_value', v)}
              onDurationChange={(v) => handleChange('referrer_reward_duration', v)}
              description="Reward given to the customer who refers someone"
              prefix="referrer"
            />
          </Card>

          <Card title="Referee Rewards" padding="lg">
            <RewardEditor
              type={config.referee_reward_type}
              value={config.referee_reward_value}
              duration={config.referee_reward_duration}
              onTypeChange={(v) => handleChange('referee_reward_type', v)}
              onValueChange={(v) => handleChange('referee_reward_value', v)}
              onDurationChange={(v) => handleChange('referee_reward_duration', v)}
              description="Reward given to the new customer being referred"
              prefix="referee"
            />
          </Card>

          <Card title="Requirements" padding="lg">
            <Input
              label="Minimum Purchase Amount"
              type="number"
              value={String(config.minimum_purchase)}
              onChange={(e) => handleChange('minimum_purchase', parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
              helperText="Minimum order/invoice total (USD) to qualify for referral rewards. 0 = no minimum."
            />
          </Card>

          <Card title="Share Messages" padding="lg">
            <div className="space-y-6">
              <Textarea
                label="Share Message"
                value={config.share_message}
                onChange={(e) => handleChange('share_message', e.target.value)}
                rows={3}
                helperText="Default message when customers share their referral link"
              />
              <Input
                label="Email Subject"
                value={config.email_subject}
                onChange={(e) => handleChange('email_subject', e.target.value)}
                helperText="Subject for referral invitation emails"
              />
              <Textarea
                label="Email Template"
                value={config.email_template}
                onChange={(e) => handleChange('email_template', e.target.value)}
                rows={4}
                helperText="Use {{referrer_name}} for personalization"
              />
            </div>
          </Card>

          {(config.stripe_referrer_coupon_id || config.stripe_referee_coupon_id) && (
            <Card title="Provisioned Stripe Coupons" padding="lg">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-stone-600">Referrer coupon</dt>
                  <dd className="font-mono text-stone-900">
                    {config.stripe_referrer_coupon_id || <span className="text-stone-400">— (points reward)</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-stone-600">Referee coupon</dt>
                  <dd className="font-mono text-stone-900">
                    {config.stripe_referee_coupon_id || <span className="text-stone-400">— (points reward)</span>}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-xs text-stone-500">
                Coupons are auto-created/refreshed on save. Changing reward type, value, or duration
                triggers a re-mint.
              </p>
            </Card>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isSaving}
          >
            <Save className="w-5 h-5 mr-2" aria-hidden="true" />
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
}

interface RewardEditorProps {
  type: RewardType;
  value: number;
  duration: number;
  onTypeChange: (v: RewardType) => void;
  onValueChange: (v: number) => void;
  onDurationChange: (v: number) => void;
  description: string;
  prefix: string;
}

function RewardEditor({
  type, value, duration, onTypeChange, onValueChange, onDurationChange, description, prefix,
}: RewardEditorProps) {
  const usesValue = rewardTypeUsesValue(type);
  const usesDuration = rewardTypeUsesDuration(type);
  return (
    <div className="space-y-4">
      <p className="text-sm text-stone-600">{description}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label htmlFor={`${prefix}_reward_type`} className="block text-sm font-medium text-stone-700 mb-1">
            Reward Type
          </label>
          <select
            id={`${prefix}_reward_type`}
            value={type}
            onChange={(e) => onTypeChange(e.target.value as RewardType)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="free_month">Free month(s)</option>
            <option value="percentage">Percentage discount</option>
            <option value="fixed">Fixed amount off</option>
            <option value="points">Loyalty points</option>
          </select>
        </div>
        {usesValue && (
          <Input
            label={type === 'percentage' ? 'Percent off' : type === 'fixed' ? 'Dollars off' : 'Points'}
            type="number"
            value={String(value)}
            onChange={(e) => onValueChange(parseFloat(e.target.value) || 0)}
            min="0"
            step={type === 'points' ? '1' : '0.01'}
          />
        )}
        {usesDuration && (
          <Input
            label={type === 'free_month' ? 'Months free' : 'Applies to N invoices/orders'}
            type="number"
            value={String(duration)}
            onChange={(e) => onDurationChange(parseInt(e.target.value, 10) || 1)}
            min="1"
            step="1"
          />
        )}
      </div>
    </div>
  );
}
